import { useState, useEffect, useCallback } from 'react';
import {
  collection, query, orderBy,
  onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, getDoc, setDoc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';
import { uploadImageToR2 } from '../services/r2UploadService';

// ---------------------------------------------------------------------------
// Path helpers — mirrors iOS CareProfileRepository
// Firestore path: users/{userId}/careProfiles/{profileId}
// ---------------------------------------------------------------------------

const careProfilesCol = (userId) =>
  collection(db, 'users', userId, 'careProfiles');

const careProfileDoc = (userId, profileId) =>
  doc(db, 'users', userId, 'careProfiles', profileId);

const userDoc = (userId) =>
  doc(db, 'users', userId);

// ---------------------------------------------------------------------------
// useCareProfiles
// Real-time listener on users/{uid}/careProfiles subcollection.
// Exposes full CRUD + image upload + group name read/write.
// ---------------------------------------------------------------------------

export const useCareProfiles = () => {
  const { user } = useAuth();
  const [careProfiles, setCareProfiles] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [groupName,    setGroupName]    = useState('');

  // ── Real-time listener ────────────────────────────────────────────────────

  useEffect(() => {
    if (!user) {
      setCareProfiles([]);
      setLoading(false);
      return;
    }

    const q = query(
      careProfilesCol(user.uid),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        setCareProfiles(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('useCareProfiles listener:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // ── Group name ────────────────────────────────────────────────────────────

  const loadGroupName = useCallback(async () => {
    if (!user) return;
    try {
      const snap = await getDoc(userDoc(user.uid));
      setGroupName(snap.data()?.careGroupName || '');
    } catch (err) {
      console.error('loadGroupName:', err);
    }
  }, [user]);

  const saveGroupName = useCallback(async (name) => {
    if (!user) return;
    try {
      setGroupName(name);
      await setDoc(userDoc(user.uid), {
        careGroupName: name,
        updatedAt: new Date(),
      }, { merge: true });
    } catch (err) {
      console.error('saveGroupName:', err);
    }
  }, [user]);

  // ── Create ────────────────────────────────────────────────────────────────

  const createProfile = useCallback(async (profileData) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const ref = await addDoc(careProfilesCol(user.uid), {
        userId:              user.uid,
        name:                profileData.name?.trim() || '',
        relationship:        profileData.relationship || 'Other',
        birthYear:           profileData.birthYear    || null,
        profileImageURL:     profileData.profileImageURL || null,
        tags:                profileData.tags               || [],
        allergies:           profileData.allergies          || [],
        dietaryRestrictions: profileData.dietaryRestrictions || [],
        notes:               profileData.notes              || null,
        isAdmin:             profileData.isAdmin            || false,
        assignedRecipeIds:   profileData.assignedRecipeIds  || [],
        assignedPantryIds:   profileData.assignedPantryIds  || [],
        createdAt:           new Date(),
        updatedAt:           new Date(),
      });
      return ref.id;
    } catch (err) {
      console.error('createProfile:', err);
      throw err;
    }
  }, [user]);

  // ── Update ────────────────────────────────────────────────────────────────

  const updateProfile = useCallback(async (profileId, profileData) => {
    if (!user || !profileId) throw new Error('Missing user or profileId');
    try {
      await updateDoc(careProfileDoc(user.uid, profileId), {
        name:                profileData.name?.trim()         || '',
        relationship:        profileData.relationship          || 'Other',
        birthYear:           profileData.birthYear             || null,
        tags:                profileData.tags                  || [],
        allergies:           profileData.allergies             || [],
        dietaryRestrictions: profileData.dietaryRestrictions   || [],
        notes:               profileData.notes                 || null,
        assignedRecipeIds:   profileData.assignedRecipeIds     || [],
        assignedPantryIds:   profileData.assignedPantryIds     || [],
        updatedAt:           new Date(),
      });
    } catch (err) {
      console.error('updateProfile:', err);
      throw err;
    }
  }, [user]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const deleteProfile = useCallback(async (profileId) => {
    if (!user || !profileId) return;
    try {
      await deleteDoc(careProfileDoc(user.uid, profileId));
    } catch (err) {
      console.error('deleteProfile:', err);
      throw err;
    }
  }, [user]);

  // ── Profile image upload — reuses r2UploadService ────────────────────────

  const uploadProfileImage = useCallback(async (profileId, file) => {
    if (!user || !profileId || !file) throw new Error('Missing params');
    try {
      const result = await uploadImageToR2(file, 'user', profileId, 'avatar');
      const url    = result.urls.medium;
      await updateDoc(careProfileDoc(user.uid, profileId), {
        profileImageURL: url,
        updatedAt:       new Date(),
      });
      return url;
    } catch (err) {
      console.error('uploadProfileImage:', err);
      throw err;
    }
  }, [user]);

  // ── Assign recipes ────────────────────────────────────────────────────────

  const assignRecipes = useCallback(async (profileId, recipeIds) => {
    if (!user || !profileId) return;
    try {
      await updateDoc(careProfileDoc(user.uid, profileId), {
        assignedRecipeIds: recipeIds,
        updatedAt:         new Date(),
      });
    } catch (err) {
      console.error('assignRecipes:', err);
      throw err;
    }
  }, [user]);

  // ── Assign pantries ───────────────────────────────────────────────────────

  const assignPantries = useCallback(async (profileId, pantryIds) => {
    if (!user || !profileId) return;
    try {
      await updateDoc(careProfileDoc(user.uid, profileId), {
        assignedPantryIds: pantryIds,
        updatedAt:         new Date(),
      });
    } catch (err) {
      console.error('assignPantries:', err);
      throw err;
    }
  }, [user]);

  // ── Computed ──────────────────────────────────────────────────────────────

  // Non-admin profiles — shown in the care profiles list
  const displayProfiles = careProfiles.filter(p => !p.isAdmin);

  // Admin profile — the user's own profile entry
  const adminProfile = careProfiles.find(p => p.isAdmin) || null;

  return {
    careProfiles,
    displayProfiles,
    adminProfile,
    loading,
    groupName,
    loadGroupName,
    saveGroupName,
    createProfile,
    updateProfile,
    deleteProfile,
    uploadProfileImage,
    assignRecipes,
    assignPantries,
  };
};